import { useRef } from "react";
import { PayCheckout } from "@/features/invoicing/pay/pay-checkout";
import { readPayToken } from "@/features/invoicing/pay/token";

/**
 * `/pay` — the public checkout for a stablecoin invoice (page spec
 * `invoice-pay-checkout`, spec PAY-00/PAY-01).
 *
 * ## Route
 *
 * Deliberately outside the authenticated shell: `App.tsx` lists `/pay` in
 * `PUBLIC_PATHS`, so `RequireAuth` never bounces an anonymous payer to the
 * sign-in screen, and the route is wrapped in the `invoicing` `FeatureGate`, so
 * with the flag off the path is not reachable at all. Both of those live in
 * `App.tsx` and are not this file's to change.
 *
 * ## Where the token comes from
 *
 * The design's link is `/pay/<unguessable token>` (spec line 69, G15), so the
 * path segment is read first; `#token=…` is read second, for links minted
 * before the route accepts a segment, and is stripped from the address bar on
 * the first read. **A token in the query string is ignored**, not honoured —
 * see `token.ts`, which also owns the URL-safe and length checks. Nothing here
 * logs the token, and nothing on this page can put it in a URL: the receipt
 * link and every control are same-origin and the document carries
 * `referrer: no-referrer`.
 *
 * ## One read per page load
 *
 * The token is read once and held in a ref rather than re-read every render.
 * Reading is not idempotent — the fragment is consumed the first time — so a
 * second read would report "no token" for a link that has one. The lazy-ref
 * form also survives React's development double-render, which would otherwise
 * consume the fragment on the first pass and load a not-active page on the
 * second.
 *
 * ## The backend
 *
 * The pay contract's routes have no backend in this environment, so a real
 * token reaches the service and gets a transport refusal, which the page shows
 * as an error state with a retry — never as sample data, and never as an
 * address. See `features/invoicing/pay/attestation.ts` for why no deposit
 * address is rendered in this build even when a service answers.
 */
export default function PayCheckoutPage() {
  const tokenRef = useRef<string | null | undefined>(undefined);
  if (tokenRef.current === undefined) {
    tokenRef.current = readPayToken()?.token ?? null;
  }

  return <PayCheckout token={tokenRef.current} />;
}
